import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {PGPApi, PGPKeyDetails} from "components/src/api/admin/generated";
import {Download} from "components/src/common/Download";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const pgpApi = new PGPApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface PGPKeyRingExportDialogProps {
    keys : PGPKeyDetails[]
    onClose : OnClose
}

export const PGPKeyRingExportDialog = ({keys, onClose} : PGPKeyRingExportDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * True if secret key export is selected
     */
    const [exportSecretKeys, setExportSecretKeys] = useState<boolean>(false);
    /**
     * The export password (only used when exporting secret keys)
     */
    const [password, setPassword] = useState<string>("");
    /**
     * If true, a Download component will be loaded which will download the binary property
     */
    const [download, setDownload] = useState(false);
    /**
     * The blob containing the binary file to download
     */
    const [blob, setBlob] = useState<Blob>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * Return true if at least one private key is selected.
     */
    const isSecretKeyWithinSelection = () => {
        return keys.some(key => key.privateKeyAlias !== undefined && key.privateKeyAlias?.length > 0);
    }

    const exportCertificates = () =>
    {
        const ids = keys.map(key => key.id);

        // the REST call depends on whether secret keys are exported or not
        const exportFunction = exportSecretKeys ?
             async () => {
                return await pgpApi.pGPControllerExportSecretKeys({ids, password}, {responseType : 'blob'})
            } :
            async () => {
                return await pgpApi.pGPControllerExportPublicKeys(ids, {responseType : 'blob'})
            }

        // NOTE: responseType must be set to 'blob' because the response is binary
        exportFunction().then((response) =>
        {
            setBlob(new Blob([response.data], {type: "application/pgp-keys"}));

            // enable the Download component
            setDownload(true);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error exporting PGP keys", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
    }

    /**
     * Is called when the download started. This should reset download to make sure the Download component is no
     * longer added.
     */
    const onDownload = () =>
    {
        // reset password
        setPassword("");
        // disable the Download component
        setDownload(false);
        onClose(DialogCloseCause.Succes);
    }

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        // reset password
        setPassword("");
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("pgp.export-selected-keys")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>

                <Form.Group className="mb-3">
                    <Form.Check
                        type="switch"
                        label={t("pgp.export-secret-keys")}
                        disabled={!isSecretKeyWithinSelection()}
                        onChange={(e) => setExportSecretKeys(e.target.checked)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>{t("pgp.secret-key-password")}</Form.Label>
                    <Form.Control type="password"
                                  placeholder="Password"
                                  disabled={!exportSecretKeys}
                                  onChange={e => setPassword(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={exportCertificates}
                        disabled={exportSecretKeys && password.length === 0}>
                    {t("common.export")}
                </Button>
            </Modal.Footer>

            {download && blob !== undefined && (
                <Download blob={blob}
                          filename="pgp-keys.asc"
                          onDownload={onDownload} />
            )}
        </Modal>
    )
}
